import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort, fmtPct } from "../lib/format";
import { computeDelta, type DeltaResult } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { KpiTile } from "../components/intelligence/KpiTile";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import type { TireOverviewDto } from "@bi/types";
import { MoveTireModal, type MoveTirePreset } from "../features/tires/MoveTireModal";
import { TabBar, ConditionBadge, fmtMm, useTireCapabilities } from "../features/tires/tire-ui";
import { TireListTab } from "../features/tires/tabs/TireListTab";
import { TireMovementsTab } from "../features/tires/tabs/TireMovementsTab";
import { TireInspectionsTab } from "../features/tires/tabs/TireInspectionsTab";
import { TireRetreadsTab } from "../features/tires/tabs/TireRetreadsTab";
import { TireSetupTab } from "../features/tires/tabs/TireSetupTab";
import { LoadMoreBar, LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import {
  IconTruck, IconAlertTriangle, IconDollar, IconPlus,
  IconTire, IconCheck, IconEye, IconRefresh, IconSync, IconX, IconLineChart,
  IconArrowUp, IconArrowDown, IconChevronRight, IconDatabase, IconClipboard, IconBarChart,
} from "../components/icons";
import type { TransportTireDashboardDto } from "@bi/types";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import type { CardMemoryRef } from "../components/card-memory/card-memory";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

/** Barrinha fina de "% do total" colorida — mesma cor da fatia equivalente no donut. */
function ShareBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

/**
 * Cabeçalho de seção da Visão Geral: a aba junta 2 painéis de fontes
 * diferentes (operação no SIAG × acervo da integração), e sem um separador
 * claro os números de um pareciam contradizer os do outro.
 */
function SectionHeader({ icon, title, subtitle, children }: {
  icon: ReactNode; title: string; subtitle: string; children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b border-surface-border">
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-9 h-9 rounded-lg bg-tm-50 text-tm-600 flex items-center justify-center flex-shrink-0">{icon}</span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 leading-tight">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {children && <div className="flex flex-wrap items-center gap-1.5">{children}</div>}
    </div>
  );
}

/** Chip discreto de metadado (fonte, contagem) ao lado do cabeçalho de seção. */
function MetaChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-surface-muted border border-surface-border px-2.5 py-0.5 text-[11px] text-gray-500">
      {children}
    </span>
  );
}

/** Sulco em mm com 2 casas (fmtNumber arredonda demais p/ 2,5mm). */
const fmt2 = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

const MONTH_PT_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** "2025-08" → "Ago/25". */
function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTH_PT_SHORT[parseInt(m ?? "1", 10) - 1] ?? ym}/${(y ?? "").slice(-2)}`;
}

/** "ALMOXARIFADO" → "Almoxarifado". */
function titleCase(s: string): string {
  return s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

// ── Paletas (design docs/frota-bi/design/pneus.png — mesmas do app Finatto) ─
// Milimetragem: vermelho (gasto) → verde (cheio); a cor segue a FAIXA.
const MM_LEGEND: Array<{ range: string; display: string; color: string }> = [
  { range: "0–3 mm",   display: "0–3",   color: "#EF4444" },
  { range: "3–8 mm",   display: "3–8",   color: "#F97316" },
  { range: "8–12 mm",  display: "8–12",  color: "#3B82F6" },
  { range: "12–18 mm", display: "12–18", color: "#14B8A6" },
  { range: ">18 mm",   display: ">18",   color: "#22C55E" },
];
// Vida: Novo → R5, do verde ao vermelho (quanto mais recapes, mais no fim da vida).
const LIFE_STAGES: Array<{ stage: string; color: string }> = [
  { stage: "Novo", color: "#22C55E" },
  { stage: "R1",   color: "#14B8A6" },
  { stage: "R2",   color: "#3B82F6" },
  { stage: "R3",   color: "#A855F7" },
  { stage: "R4",   color: "#F59E0B" },
  { stage: "R5",   color: "#EF4444" },
];
const NEUTRAL_SLICE = "#9CA3AF";

// Categórica validada (dataviz) p/ descartes por origem — seção de detalhes.
const ORIGIN_COLORS: Record<string, string> = {
  OPERACIONAL: "#3B82F6",
  MOTORISTA: "#F97316",
  REFORMADOR: "#10B981",
  FABRICANTE: "#8B5CF6",
  "VENDA DE PNEUS": "#E11D48",
  CLIENTE: "#0891B2",
};

const CHART_TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" } as const;

// ── Blocos visuais do design ────────────────────────────────────────────────

function DeltaLine({ delta, cmp }: { delta: DeltaResult | null; cmp: string }) {
  if (!delta || delta.pct == null) return null;
  const color = delta.trend === "flat" ? "text-gray-400" : delta.isGood ? "text-tm-600" : "text-danger-600";
  return (
    <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${color}`}>
      {delta.trend === "up" ? <IconArrowUp className="w-3.5 h-3.5" /> : delta.trend === "down" ? <IconArrowDown className="w-3.5 h-3.5" /> : null}
      {fmtPct(Math.abs(delta.pct))} <span className="font-normal text-gray-400">{cmp}</span>
    </p>
  );
}

/** KPI compacto do topo: ícone em círculo, valor, delta e linha descritiva. */
function TopKpi({ icon, label, value, delta, cmp, sub, extra, warning, loading, memory }: {
  icon: ReactNode; label: string; value: string;
  delta?: DeltaResult | null; cmp?: string; sub: string; extra?: string;
  warning?: boolean; loading?: boolean;
  /** memória de cálculo: com ela o card abre a composição do número */
  memory?: CardMemoryRef | null;
}) {
  if (loading) {
    return (
      <div className="card p-4 space-y-3 animate-pulse">
        <div className="h-8 w-8 bg-gray-200 rounded-full" />
        <div className="h-6 w-28 bg-gray-200 rounded" />
        <div className="h-3 w-20 bg-gray-100 rounded" />
      </div>
    );
  }
  return (
    <CardMemoryTrigger memory={memory ? { title: label, ...memory } : null}>
    <div className={`card p-4 relative h-full ${warning ? "bg-warning-50 border-warning-400/40" : ""}`}>
      {warning && <span className="badge-amber absolute top-3 right-3">Atenção</span>}
      <div className="flex items-center gap-2.5">
        <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${warning ? "bg-warning-100 text-warning-600" : "bg-tm-50 text-tm-600"}`}>
          {icon}
        </span>
        <p className="text-xs font-medium text-gray-500 leading-tight">{label}</p>
      </div>
      <p className="mt-2.5 text-xl font-bold text-gray-900 truncate" title={value}>{value}</p>
      <DeltaLine delta={delta ?? null} cmp={cmp ?? ""} />
      {extra && <p className="mt-1 text-xs font-medium text-gray-600">{extra}</p>}
      <p className="mt-2 pt-2 border-t border-surface-border text-xs text-gray-400 leading-snug">{sub}</p>
    </div>
    </CardMemoryTrigger>
  );
}

/** Card do acervo: valor grande + barra segmentada por unidade + quebra Almox/Galpão. */
function AcervoCard({ icon, label, value, split, subLabel, warning, loading, memory }: {
  icon: ReactNode; label: string; value: number;
  memory?: CardMemoryRef | null;
  /** [{name: "Almoxarifado", value: 2404}, {name: "Galpão", value: 158}] */
  split?: Array<{ name: string; value: number }>;
  subLabel?: string; warning?: boolean; loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="card p-4 space-y-3 animate-pulse">
        <div className="h-3 w-24 bg-gray-200 rounded" />
        <div className="h-7 w-16 bg-gray-200 rounded" />
        <div className="h-2 w-full bg-gray-100 rounded" />
      </div>
    );
  }
  const total = split?.reduce((s, x) => s + x.value, 0) ?? 0;
  const SEGMENT_COLORS = ["bg-tm-500", "bg-info-500", "bg-warning-500", "bg-danger-500"];
  return (
    <CardMemoryTrigger memory={memory ? { title: label, expected: value, ...memory } : null}>
    <div className="card p-4 h-full">
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${warning && value > 0 ? "bg-warning-100 text-warning-600" : "bg-tm-50 text-tm-600"}`}>
          {icon}
        </span>
        <p className="text-xs font-medium text-gray-500 leading-tight">{label}</p>
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-900">{fmtNumber(value)}</p>
      {split ? (
        <>
          <div className="mt-2.5 flex h-1.5 rounded-full overflow-hidden bg-surface-muted">
            {total > 0 && split.map((s, i) => (
              <div key={s.name} className={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                style={{ width: `${(s.value / total) * 100}%` }} />
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 text-xs">
            {split.map((s) => (
              <span key={s.name} className="text-gray-400 truncate">
                {s.name} <span className="font-semibold text-gray-600">{fmtNumber(s.value)}</span>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-2 text-xs text-gray-400">{subLabel}</p>
      )}
    </div>
    </CardMemoryTrigger>
  );
}

/** Donut com total no centro e legenda lateral "rótulo — % (n)". */
function DonutWithLegend({ data, total }: {
  data: Array<{ name: string; value: number; color: string }>;
  total: number;
}) {
  const withValue = data.filter((d) => d.value > 0);
  return (
    <div className="h-full flex items-center gap-3">
      <div className="relative h-full flex-shrink-0" style={{ width: "46%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={withValue} dataKey="value" nameKey="name" cx="50%" cy="50%"
              innerRadius="58%" outerRadius="92%" stroke="#ffffff" strokeWidth={2}>
              {withValue.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip formatter={(v: number) => [`${fmtNumber(v)} pneus`, ""]} contentStyle={CHART_TOOLTIP_STYLE} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-xl font-bold text-gray-900">{fmtNumber(total)}</p>
          <p className="text-[11px] text-gray-400">Total</p>
        </div>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: d.color }} />
            <span className="text-gray-500 truncate">{d.name}</span>
            <span className="ml-auto font-medium text-gray-700 whitespace-nowrap">
              {total > 0 ? `${((d.value / total) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%` : "0%"}
              <span className="text-gray-400 font-normal"> ({fmtNumber(d.value)})</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Painel de estatística da fileira inferior (Valor/KM desperdiçado). */
function WastePanel({ icon, label, value, sub, note, anchor, memory }: {
  icon: ReactNode; label: string; value: string; sub: string; note: string; anchor?: string;
  memory?: CardMemoryRef | null;
}) {
  return (
    <CardMemoryTrigger memory={memory ? { title: label, ...memory } : null}>
    <div className="card p-4 flex flex-col h-full">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-full bg-tm-50 text-tm-600 flex items-center justify-center flex-shrink-0">{icon}</span>
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className="mt-2.5 text-xl font-bold text-gray-900 truncate" title={value}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
      <p className="mt-2 text-xs text-gray-400 leading-snug flex-1">{note}</p>
      {anchor && (
        <a href={anchor} onClick={(e) => e.stopPropagation()} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-info-600 hover:text-info-700">
          Ver análise detalhada <IconChevronRight className="w-3 h-3" />
        </a>
      )}
    </div>
    </CardMemoryTrigger>
  );
}

/** Lista de barras horizontais (marcas/medidas) — seção de detalhes. */
function BarList({ items, unit }: { items: Array<{ label: string; count: number }>; unit: string }) {
  const top = items.slice(0, 8);
  const rest = items.slice(8).reduce((s, i) => s + i.count, 0);
  const peak = Math.max(...top.map((i) => i.count), 1);
  return (
    <div className="space-y-2.5">
      {top.map((i) => (
        <div key={i.label} className="flex items-center gap-3">
          <span className="w-36 flex-shrink-0 text-xs text-gray-500 truncate" title={i.label}>{i.label}</span>
          <div className="flex-1 h-2.5 bg-surface-muted rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-tm-500" style={{ width: `${(i.count / peak) * 100}%` }} />
          </div>
          <span className="w-14 text-right text-sm font-semibold text-gray-800">{fmtNumber(i.count)}</span>
        </div>
      ))}
      {rest > 0 && (
        <p className="text-xs text-gray-400 pt-1">+ {fmtNumber(rest)} {unit} em outras {items.length - top.length} categorias</p>
      )}
    </div>
  );
}

/**
 * Indicadores do acervo vindos da integração (Finatto/SinerSoft) — o painel que
 * já existia em /frota/pneus, preservado como está dentro da aba Visão Geral.
 */
function TireBiDashboard() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "tires", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportTireDashboardDto>(
        `/dashboards/transport/tires?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const p = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. mês anterior";

  const byDay      = data?.byDay      ?? [];
  const topVehicles = data?.topVehicles ?? [];
  const byBrand    = data?.byBrand    ?? [];
  const nearChange = data?.nearChange ?? [];
  const derived    = data?.derivedSpend;
  const valuation  = data?.valuation;
  const byStatus   = data?.byStatus ?? [];
  const panel      = data?.finattoPanel;
  const charts     = data?.finattoCharts;
  const waste      = data?.waste;
  const returns    = data?.supplierReturns;
  const issue      = data?.sourceIssue;
  const tireDetails = data?.tireDetails ?? [];
  const bySource   = data?.bySource ?? [];

  const costDelta = computeDelta(s?.cost, p?.cost, { direction: "higher_is_worse" });

  // Filtros client-side da tabela (marca / status)
  const [brandFilter, setBrandFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const brandOptions = useMemo(
    () => Array.from(new Set(tireDetails.map((t) => t.brand).filter(Boolean))).sort() as string[],
    [tireDetails],
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(tireDetails.map((t) => t.status).filter(Boolean))).sort() as string[],
    [tireDetails],
  );
  const filteredTires = useMemo(
    () => tireDetails.filter((t) =>
      (!brandFilter || t.brand === brandFilter) && (!statusFilter || t.status === statusFilter)),
    [tireDetails, brandFilter, statusFilter],
  );
  // A lista vem inteira da API; antes a tabela parava em 100 sem avisar.
  const tireList = useProgressiveList(filteredTires, 100);

  // Acervo consolidado: soma Almoxarifado + Galpão (todas as unidades Finatto).
  const panelTotal = useMemo(() => {
    if (!panel || panel.units.length === 0) return null;
    const counts = { ativos: 0, montados: 0, disponiveis: 0, emAnalise: 0, analiseDescarte: 0, reformador: 0, emTransferencia: 0, naoLocalizados: 0, outros: 0 };
    const values = { ativos: 0, rodando: 0, novos: 0, disponiveis: 0, recapados: 0, analiseEReformador: 0, unvaluedCount: 0 };
    for (const u of panel.units) {
      for (const k of Object.keys(counts) as Array<keyof typeof counts>) counts[k] += u.counts[k];
      for (const k of Object.keys(values) as Array<keyof typeof values>) values[k] += u.values[k];
    }
    return { counts, values };
  }, [panel]);

  const unitTitle = (u: { unitName: string | null; unitCode: number }) =>
    u.unitName ? titleCase(u.unitName) : `Unidade ${u.unitCode}`;
  /** Quebra por unidade p/ a barra segmentada dos cards do acervo. */
  const unitSplit = (get: (u: NonNullable<typeof panel>["units"][number]) => number) =>
    panel ? panel.units.map((u) => ({ name: unitTitle(u), value: get(u) })) : undefined;

  // Donuts (design): legenda fixa — categoria zerada aparece com (0), como no print.
  const lifeDonut = useMemo(() => {
    const counts = new Map((charts?.life ?? []).map((l) => [l.stage, l.count]));
    const fixed = LIFE_STAGES.map(({ stage, color }) => ({ name: stage, value: counts.get(stage) ?? 0, color }));
    const extras = (charts?.life ?? [])
      .filter((l) => !LIFE_STAGES.some((f) => f.stage === l.stage))
      .map((l) => ({ name: l.stage, value: l.count, color: NEUTRAL_SLICE }));
    return [...fixed, ...extras];
  }, [charts]);
  const lifeTotal = lifeDonut.reduce((sum, d) => sum + d.value, 0);

  const mmDonut = useMemo(() => {
    const counts = new Map((charts?.mm.buckets ?? []).map((b) => [b.range, b.count]));
    return MM_LEGEND.map(({ range, display, color }) => ({ name: display, value: counts.get(range) ?? 0, color }));
  }, [charts]);
  const mmTotal = mmDonut.reduce((sum, d) => sum + d.value, 0);

  // Pivot dos descartes p/ barras empilhadas (seção de detalhes).
  const discards = useMemo(() => {
    if (!charts) return { rows: [] as Array<Record<string, number | string>>, origins: [] as string[], total: 0 };
    const originTotals = new Map<string, number>();
    for (const m of charts.discardsByMonth)
      for (const o of m.origins) originTotals.set(o.origin, (originTotals.get(o.origin) ?? 0) + o.count);
    const origins = [...originTotals.entries()].sort((a, b) => b[1] - a[1]).map(([o]) => o);
    const rows = charts.discardsByMonth.map((m) => {
      const row: Record<string, number | string> = { month: monthLabel(m.month) };
      for (const o of m.origins) row[o.origin] = o.count;
      return row;
    });
    const total = [...originTotals.values()].reduce((sum, v) => sum + v, 0);
    return { rows, origins, total };
  }, [charts]);

  const vehiclesTotalCost = topVehicles.reduce((sum, v) => sum + v.cost, 0);
  const vehiclesHaveCost = vehiclesTotalCost > 0;
  const maxVehicleEvents = Math.max(1, ...topVehicles.map((v) => v.eventCount));
  // Mesmo padrão das demais telas de ranking (antes: um "ver ranking completo"
  // próprio, que abria tudo de uma vez e não dizia quantos itens havia).
  // Sem valor por placa, a API devolve tudo empatado em R$ 0 (ordem
  // arbitrária); o ranking passa a ser pelo nº de eventos.
  const rankedVehicles = useMemo(
    () => (vehiclesHaveCost ? topVehicles : [...topVehicles].sort((a, b) => b.eventCount - a.eventCount)),
    [topVehicles, vehiclesHaveCost],
  );
  const vehicleList = useProgressiveList(rankedVehicles);
  const nearChangeList = useProgressiveList(nearChange);

  const RETURN_ROWS: Array<{ label: string; value: number | null; color: string }> = [
    { label: "Recape",    value: returns?.recape ?? null,    color: "text-danger-600" },
    { label: "Conserto",  value: returns?.conserto ?? null,  color: "text-info-600" },
    { label: "Recusado",  value: returns?.recusado ?? null,  color: "text-warning-600" },
    { label: "Condenado", value: returns?.condenado ?? null, color: "text-teal-600" },
    { label: "Garantia",  value: returns?.garantia ?? null,  color: "text-purple-600" },
  ];

  return (
    <div className="space-y-6">
      {dataError && <QueryErrorBanner what="os indicadores do acervo de pneus" onRetry={() => { dataRefetch(); }} />}
      <SectionHeader icon={<IconDatabase className="w-4 h-4" />} title="Acervo de pneus (integração)"
        subtitle="Valor, vida útil, perdas e pneus próximos de troca no cadastro da integração · período do filtro global">
        {bySource.map((src) => (
          <MetaChip key={src.source}>
            <span className="capitalize">{src.source}</span>
            <span className="font-semibold text-gray-700">{fmtNumber(src.count)}</span>
          </MetaChip>
        ))}
      </SectionHeader>

      {/* Sem dado nenhum na fonte ativa: a tela explica o porquê em vez de
          mostrar uma parede de cards zerados. */}
      {issue && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-4">
          <p className="text-sm font-semibold text-amber-900">
            Sem dados de pneus na fonte ativa ({data?.activeSource})
            {issue.syncStatus === "running" && " · carga em andamento"}
          </p>
          <p className="text-sm text-amber-800 mt-1">{issue.message}</p>
        </div>
      )}

      {/* Faixa de KPIs (design pneus.png) */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {valuation ? (
          <TopKpi icon={<IconDollar className="w-4 h-4" />} label="Valor dos pneus ativos" memory={{ cardKey: "pneus.valor-ativos", from, to, expected: valuation?.totalValue }}
            value={fmtBRL(valuation.totalValue)} sub="Investimento total em pneus ativos"
            loading={isLoading} />
        ) : (
          <TopKpi icon={<IconDollar className="w-4 h-4" />} label="Gasto pneus (estimado)" memory={{ cardKey: "pneus.gasto-estimado", from, to, expected: derived?.total }}
            value={derived ? fmtBRL(derived.total) : "—"} sub="Estimado por contas a pagar de fornecedores de pneu"
            loading={isLoading} />
        )}
        {panelTotal && (
          <TopKpi icon={<IconTruck className="w-4 h-4" />} label="Valor pneus rodando" memory={{ cardKey: "pneus.valor-rodando", from, to, expected: panelTotal?.values.rodando }}
            value={fmtBRL(panelTotal.values.rodando)} sub="Valor residual dos pneus montados"
            loading={isLoading} />
        )}
        <TopKpi icon={<IconDollar className="w-4 h-4" />} label="Gasto no período" memory={{ cardKey: "pneus.gasto-periodo", from, to, expected: s?.cost }}
          value={s ? fmtBRL(s.cost) : "—"} delta={costDelta} cmp={cmp}
          sub="Eventos de movimentação e recapagem" loading={isLoading} />
        <TopKpi icon={<IconRefresh className="w-4 h-4" />} label="Pneus recapados (acervo)" memory={{ cardKey: "pneus.recapados", expected: s?.recappedTireCount }}
          value={s ? fmtNumber(s.recappedTireCount) : "—"}
          sub="Acervo de recapagens disponível" loading={isLoading} />
        <TopKpi icon={<IconLineChart className="w-4 h-4" />} label="Vida útil média" memory={{ cardKey: "pneus.vida-util", expected: s?.avgLifeKm }}
          value={s ? `${fmtNumber(Math.round(s.avgLifeKm))} km` : "—"}
          sub="Km acumulado médio dos ativos" loading={isLoading} />
        <TopKpi icon={<IconAlertTriangle className="w-4 h-4" />} label="Próximos de troca" memory={{ cardKey: "pneus.proximos-troca", expected: s?.nearChangeCount }}
          value={s ? fmtNumber(s.nearChangeCount) : "—"} extra="≥ 90% da vida útil"
          sub="Pneus próximos do limite seguro"
          warning={!!s && s.nearChangeCount > 0} loading={isLoading} />
      </div>

      {/* Acervo de Pneus — visão consolidada com quebra por unidade */}
      {panel && panelTotal && (
        <div className="card p-5 space-y-4">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-gray-900">
              Acervo de Pneus{" "}
              <span className="text-xs font-normal text-gray-400">
                Visão consolidada ({panel.units.map(unitTitle).join(" + ")})
              </span>
            </p>
            <a href="#tabela-pneus" onClick={() => setListOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-info-600 hover:text-info-700">
              Ver detalhes completos <IconChevronRight className="w-3 h-3" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            <AcervoCard icon={<IconTire className="w-4 h-4" />} label="Pneus ativos" memory={{ cardKey: "pneus.acervo", params: { grupo: "ativos" } }}
              value={panelTotal.counts.ativos} split={unitSplit((u) => u.counts.ativos)} loading={isLoading} />
            <AcervoCard icon={<IconTruck className="w-4 h-4" />} label="Montados" memory={{ cardKey: "pneus.acervo", params: { grupo: "montados" } }}
              value={panelTotal.counts.montados} split={unitSplit((u) => u.counts.montados)} loading={isLoading} />
            <AcervoCard icon={<IconCheck className="w-4 h-4" />} label="Disponíveis" memory={{ cardKey: "pneus.acervo", params: { grupo: "disponiveis" } }}
              value={panelTotal.counts.disponiveis} split={unitSplit((u) => u.counts.disponiveis)} loading={isLoading} />
            <AcervoCard icon={<IconEye className="w-4 h-4" />} label="Em análise" memory={{ cardKey: "pneus.acervo", params: { grupo: "emAnalise" } }}
              value={panelTotal.counts.emAnalise} split={unitSplit((u) => u.counts.emAnalise)} loading={isLoading} />
            <AcervoCard icon={<IconAlertTriangle className="w-4 h-4" />} label="Análise de descarte" memory={{ cardKey: "pneus.acervo", params: { grupo: "analiseDescarte" } }}
              value={panelTotal.counts.analiseDescarte} split={unitSplit((u) => u.counts.analiseDescarte)} loading={isLoading} />
            <AcervoCard icon={<IconRefresh className="w-4 h-4" />} label="No reformador" memory={{ cardKey: "pneus.acervo", params: { grupo: "reformador" } }}
              value={panelTotal.counts.reformador} split={unitSplit((u) => u.counts.reformador)} loading={isLoading} />
            <AcervoCard icon={<IconSync className="w-4 h-4" />} label="Em transferência" memory={{ cardKey: "pneus.acervo", params: { grupo: "emTransferencia" } }}
              value={panelTotal.counts.emTransferencia} split={unitSplit((u) => u.counts.emTransferencia)} loading={isLoading} />
            <AcervoCard icon={<IconX className="w-4 h-4" />} label="Não localizados" memory={{ cardKey: "pneus.acervo", params: { grupo: "naoLocalizados" } }}
              value={panelTotal.counts.naoLocalizados} split={unitSplit((u) => u.counts.naoLocalizados)}
              warning loading={isLoading} />
            {panel.vehicles && (
              <>
                <AcervoCard icon={<IconTruck className="w-4 h-4" />} label="Veículos com pneus" memory={{ cardKey: "pneus.veiculos-com-pneus" }}
                  value={panel.vehicles.withTires}
                  subLabel={panel.vehicles.note ? "Posições sincronizadas (contagem parcial, A7)" : "Posições sincronizadas"}
                  loading={isLoading} />
                <AcervoCard icon={<IconAlertTriangle className="w-4 h-4" />} label="Faltando pneus" memory={{ cardKey: "pneus.faltando" }}
                  value={panel.vehicles.positionsMissing} subLabel="Posições vazias em ativos"
                  warning loading={isLoading} />
              </>
            )}
          </div>

          {panelTotal.counts.outros > 0 && (
            <p className="text-xs text-amber-700">
              ⚠️ {fmtNumber(panelTotal.counts.outros)} pneu(s) com status fora da legenda conhecida. Ver abaixo, por status.
            </p>
          )}

          {byStatus.length > 0 && (
            <div className="pt-3 border-t border-surface-border flex flex-wrap items-center gap-2">
              <p className="text-xs font-medium text-gray-500 mr-1">Por status:</p>
              {byStatus.map((st) => (
                <div key={st.code} className="rounded-lg border border-surface-border px-2.5 py-1 flex items-baseline gap-1.5">
                  <span className={`text-[11px] ${st.known ? "text-gray-500" : "text-amber-600"}`} title={st.known ? st.label : `${st.label} (legenda não confirmada)`}>
                    {st.label}{!st.known && " ⚠️"}
                  </span>
                  <span className="text-xs font-semibold text-gray-800">{fmtNumber(st.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fileira de gráficos: gasto/dia · vida útil por marca · donut vida · donut mm */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <ChartCard title="Gasto com pneus por dia" subtitle="Valor (R$)" height={210}
          isEmpty={!isLoading && byDay.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-tire" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v: number) => `${(v / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Gasto"]} labelFormatter={fmtDateShort} contentStyle={CHART_TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="cost" stroke="var(--brand)" strokeWidth={2} fill="url(#grad-tire)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Vida útil média por marca" subtitle="Km acumulado médio"
          actions={<span className="text-xs text-gray-400 border border-surface-border rounded-lg px-2 py-1">Top 6 marcas</span>}
          height={210} isEmpty={!isLoading && byBrand.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byBrand.slice(0, 6)} margin={{ top: 5, right: 5, left: -5, bottom: 14 }}>
              <defs>
                <linearGradient id="grad-tire-brand-life" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="brand" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} axisLine={false}
                interval={0} angle={-28} textAnchor="end" height={42}
                tickFormatter={(b: string) => (b.length > 11 ? `${b.slice(0, 10)}…` : b)} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [`${fmtNumber(Math.round(v))} km`, "Vida útil média"]} contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="avgLifeKm" fill="url(#grad-tire-brand-life)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Vida dos pneus ativos" subtitle="Distribuição por banda de vida" height={210}
          isEmpty={!isLoading && lifeTotal === 0} loading={isLoading}>
          <DonutWithLegend data={lifeDonut} total={lifeTotal} />
        </ChartCard>

        <ChartCard title="Milimetragem dos pneus ativos"
          subtitle={`Profundidade do sulco (mm)${charts && charts.mm.unknownCount > 0 ? ` · ${fmtNumber(charts.mm.unknownCount)} sem medição` : ""}`}
          height={210} isEmpty={!isLoading && mmTotal === 0} loading={isLoading}>
          <DonutWithLegend data={mmDonut} total={mmTotal} />
        </ChartCard>
      </div>

      {/* Perdas e retornos (12 meses): faixa compacta. Antes eram 3 colunas
          estreitas esticadas até a altura das tabelas vizinhas, com metade
          do card vazio. */}
      {(waste || returns) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {waste && (
            <>
              <WastePanel icon={<IconDollar className="w-4 h-4" />} label="Valor desperdiçado" memory={{ cardKey: "pneus.desperdicio-valor", from, to, expected: waste?.valueLost }}
                value={fmtBRL(waste.valueLost)} sub={`Últimos 12 meses · ${fmtNumber(waste.tiresDiscarded)} pneus baixados`}
                note="Custo perdido com descarte prematuro e perdas (estimativa pelo residual na baixa)."
                anchor="#descartes-12m" />
              <WastePanel icon={<IconLineChart className="w-4 h-4" />} label="KM desperdiçado" memory={{ cardKey: "pneus.desperdicio-km", from, to, expected: waste?.kmLost }}
                value={`${fmtNumber(Math.round(waste.kmLost))} km`} sub="Últimos 12 meses"
                note="Quilometragem não aproveitada devido a trocas antecipadas (projeção pelo desgaste)."
                anchor="#descartes-12m" />
            </>
          )}
          {returns && (
            <CardMemoryTrigger memory={{ cardKey: "pneus.retornos-recape", from, to, expected: returns.recape, title: "Retornos do fornecedor: recape" }}>
            <div className="card p-4 flex flex-col h-full">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full bg-tm-50 text-tm-600 flex items-center justify-center flex-shrink-0"><IconRefresh className="w-4 h-4" /></span>
                <p className="text-xs font-medium text-gray-500">Retornos do fornecedor</p>
                <span className="ml-auto text-[11px] text-gray-400">Últimos 12 meses</span>
              </div>
              <div className="mt-3 grid grid-cols-5 gap-1.5">
                {RETURN_ROWS.map((r) => (
                  <div key={r.label} className="rounded-lg bg-surface-muted px-1 py-2 text-center">
                    <p className={`text-lg font-bold leading-none ${r.value != null ? r.color : "text-gray-300"}`}>
                      {r.value != null ? fmtNumber(r.value) : "—"}
                    </p>
                    <p className="mt-1 text-[10px] text-gray-500 truncate">{r.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2.5 text-[11px] text-gray-400 leading-snug">{returns.note}</p>
            </div>
            </CardMemoryTrigger>
          )}
        </div>
      )}

      {/* Duas listas lado a lado, mesma largura. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-amber-500" />
              Pneus próximos de troca
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Uso acima de 90% da vida útil (sulco ou km, conforme o que a integração informa)</p>
          </div>
          <div className="overflow-x-auto flex-1">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : nearChange.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum pneu próximo de troca. 👍</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                    <th className="pl-4 pr-2 py-2 font-medium">Pneu</th>
                    <th className="px-2 py-2 font-medium">Medida</th>
                    {/* A lista mistura pneus medidos por sulco (mm) e por km;
                        antes o cabeçalho dizia "Sulco atual" mesmo com linha em km. */}
                    <th className="px-2 py-2 font-medium text-right">Atual</th>
                    <th className="pl-2 pr-4 py-2 font-medium w-40">Vida útil consumida</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {nearChangeList.visible.map((t, i) => {
                    const critical = t.pct >= 1;
                    return (
                      <tr key={i} className="text-gray-700">
                        <td className={`pl-4 pr-2 py-2 border-l-[3px] ${critical ? "border-l-danger-500" : "border-l-warning-500"}`}>
                          <p className="font-medium text-gray-900 whitespace-nowrap">{t.fireNumber}</p>
                          <p className="text-xs text-gray-400 whitespace-nowrap">{t.plate ?? "Fora de veículo"}</p>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-gray-500">{t.size ?? "—"}</td>
                        <td className="px-2 py-2 text-right whitespace-nowrap font-medium text-gray-800">
                          {t.basis === "mm" ? `${fmt2(t.current)} mm` : `${fmtNumber(t.current)} km`}
                        </td>
                        <td className="pl-2 pr-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                              <div className={`h-full rounded-full ${critical ? "bg-danger-500" : "bg-warning-500"}`}
                                style={{ width: `${Math.min(100, t.pct * 100)}%` }} />
                            </div>
                            <span className={`w-12 text-right text-xs font-semibold ${critical ? "text-danger-600" : "text-warning-700"}`}>
                              {Math.round(t.pct * 100)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <LoadMoreBar list={nearChangeList} label="pneus" />
        </div>

        {/* Quando nenhum evento do período tem valor ligado a uma placa (caso
            da Transmasi hoje), a coluna de R$ vinha toda "R$ 0,00 · 0,00%".
            Nesse caso o card vira ranking por eventos, sem fingir valor. */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              {vehiclesHaveCost ? "Gasto com pneus por veículo" : "Movimentações de pneus por veículo"}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {vehiclesHaveCost
                ? "Valor dos eventos de movimentação e recapagem no período"
                : "Nenhum evento do período tem valor ligado a uma placa, então o ranking é pelo número de eventos"}
            </p>
          </div>
          <div className="overflow-x-auto flex-1">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                    <th className="pl-5 pr-2 py-2 font-medium w-8">#</th>
                    <th className="px-2 py-2 font-medium">Placa</th>
                    <th className="px-2 py-2 font-medium w-2/5">{vehiclesHaveCost ? "% do total" : "Eventos"}</th>
                    {vehiclesHaveCost && <th className="px-2 py-2 font-medium text-right">Eventos</th>}
                    {vehiclesHaveCost && <th className="pl-2 pr-5 py-2 font-medium text-right">Valor gasto</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {vehicleList.visible.map((v, i) => {
                    const share = vehiclesHaveCost
                      ? (v.cost / vehiclesTotalCost) * 100
                      : (v.eventCount / maxVehicleEvents) * 100;
                    return (
                      <tr key={i} className="text-gray-700">
                        <td className="pl-5 pr-2 py-2.5 text-xs font-bold text-gray-400">{i + 1}</td>
                        <td className="px-2 py-2.5 font-medium text-gray-900 whitespace-nowrap">{v.plate}</td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                              <div className="h-full rounded-full bg-tm-400" style={{ width: `${share}%` }} />
                            </div>
                            <span className="w-12 text-right text-xs font-semibold text-gray-700">
                              {vehiclesHaveCost
                                ? `${share.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
                                : fmtNumber(v.eventCount)}
                            </span>
                          </div>
                        </td>
                        {vehiclesHaveCost && <td className="px-2 py-2.5 text-right">{fmtNumber(v.eventCount)}</td>}
                        {vehiclesHaveCost && <td className="pl-2 pr-5 py-2.5 text-right font-semibold whitespace-nowrap">{v.formatted}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <LoadMoreBar list={vehicleList} label="veículos" />
        </div>
      </div>

      {/* Detalhes avançados (marcas, descartes, fabricação, medida, fornecedores):
          recolhido por padrão — é o que mais contribuía pra sensação de "parede
          de cards" na tela. <details> nativo: um link de âncora pra algo AQUI
          DENTRO (ex. "Ver análise detalhada" → #descartes-12m) abre o bloco
          automaticamente no Chrome/Edge/Firefox modernos, sem JS a mais. */}
      <details className="group">
        <summary className="card px-5 py-4 flex items-center gap-2 cursor-pointer select-none hover:bg-surface-muted/60 [&::-webkit-details-marker]:hidden marker:hidden">
          <IconChevronRight className="w-3.5 h-3.5 text-gray-400 transition-transform group-open:rotate-90" />
          <IconBarChart className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-900">Detalhes avançados</span>
          <span className="text-xs text-gray-400">· marcas, descartes, fabricação, medida e fornecedores</span>
        </summary>

        <div className="pt-4 space-y-4">
          {charts && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Marcas de Carcaças Ativas" subtitle="Pneus ativos por marca da carcaça" height={280}
                isEmpty={byBrand.length === 0} loading={isLoading}>
                <BarList items={byBrand.map((b) => ({ label: b.brand, count: b.count }))} unit="pneus" />
              </ChartCard>
              <ChartCard title="Marcas de Recapagens Ativas" subtitle="Pneus recapados ativos por marca da banda" height={280}
                isEmpty={charts.recapBrands.length === 0} loading={isLoading}>
                <BarList items={charts.recapBrands.map((b) => ({ label: b.brand, count: b.count }))} unit="pneus" />
              </ChartCard>
            </div>
          )}

          {charts && (
            <div id="descartes-12m">
              <ChartCard title="Pneus Descartados nos Últimos 12 Meses"
                subtitle={`Baixas por origem · ${fmtNumber(discards.total)} no total`}
                height={280} isEmpty={discards.total === 0} loading={isLoading}
                emptyMessage="Nenhuma baixa de pneu registrada nos últimos 12 meses.">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={discards.rows} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                    <defs>
                      {discards.origins.map((o) => (
                        <linearGradient key={o} id={`grad-discard-${o.replace(/[^a-zA-Z0-9]/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"  stopColor={ORIGIN_COLORS[o] ?? NEUTRAL_SLICE} stopOpacity={0.95} />
                          <stop offset="100%" stopColor={ORIGIN_COLORS[o] ?? NEUTRAL_SLICE} stopOpacity={0.55} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    {discards.origins.map((o) => (
                      <Bar key={o} dataKey={o} stackId="baixas" fill={`url(#grad-discard-${o.replace(/[^a-zA-Z0-9]/g, "")})`}
                        stroke="#ffffff" strokeWidth={1} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}

          {charts && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Pneus por Ano de Fabricação"
                subtitle={`DOT dos pneus ativos${charts.dot.unknownCount > 0 ? ` · ${fmtNumber(charts.dot.unknownCount)} sem DOT legível` : ""}`}
                height={240} isEmpty={charts.dot.years.length === 0} loading={isLoading}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.dot.years} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-tire-dot-year" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(v: number) => [`${fmtNumber(v)} pneus`, "Fabricados"]} contentStyle={CHART_TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="url(#grad-tire-dot-year)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Pneus por Medida" subtitle="Pneus ativos por dimensão" height={240}
                isEmpty={charts.sizes.length === 0} loading={isLoading}>
                <BarList items={charts.sizes.map((sz) => ({ label: sz.size, count: sz.count }))} unit="pneus" />
              </ChartCard>
            </div>
          )}

          {/* Gasto por fornecedor: só quando a estimativa por contas a pagar é a
              melhor informação disponível (fonte SinerSoft). */}
          {derived && (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-surface-border">
                <p className="text-sm font-semibold text-gray-900">Gasto com Pneus por Fornecedor <span className="text-xs font-normal text-amber-600">· estimativa</span></p>
                <p className="text-xs text-gray-400 mt-0.5">{derived.note}</p>
              </div>
              <div className="divide-y divide-surface-border">
                {derived.topSuppliers.length === 0 ? (
                  <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem títulos de fornecedores de pneu no período.</p>
                ) : derived.topSuppliers.map((f, i) => {
                  const sPct = derived.total > 0 ? (f.total / derived.total) * 100 : 0;
                  return (
                    <div key={i} className="px-5 py-3 flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{f.name}</p>
                        <div className="mt-1.5 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                          <div className="h-full rounded-full bg-tm-400" style={{ width: `${sPct}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-tm-700">{f.formatted}</p>
                        <p className="text-[11px] text-gray-400">{sPct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </details>

      {/* Tabela de pneus (FASE 7): recolhida por padrão. Aberta, ela sozinha
          dobrava a altura da Visão Geral (200 linhas), e a lista operacional
          completa já tem aba própria ("Pneus"). Controlada por estado para o
          link "Ver detalhes completos" do card do acervo abrir e rolar até ela. */}
      <details className="group card overflow-hidden scroll-mt-20" id="tabela-pneus" open={listOpen}
        onToggle={(e) => setListOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary className="px-5 py-4 flex items-center gap-2 cursor-pointer select-none hover:bg-surface-muted/60 [&::-webkit-details-marker]:hidden marker:hidden">
          <IconChevronRight className="w-3.5 h-3.5 text-gray-400 transition-transform group-open:rotate-90" />
          <IconClipboard className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-900">Lista do acervo</span>
          <span className="text-xs text-gray-400">· {fmtNumber(tireDetails.length)} pneus da integração, com custo/km e status</span>
        </summary>
        <div className="px-5 py-3 border-y border-surface-border flex flex-wrap items-center justify-between gap-3 bg-surface-muted/40">
          <p className="text-xs text-gray-500">
            {brandFilter || statusFilter ? `${fmtNumber(filteredTires.length)} de ${fmtNumber(tireDetails.length)} pneus com o filtro` : "Filtre por marca ou status"}
          </p>
          <div className="flex items-center gap-2">
            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
              className="text-xs border border-surface-border rounded-lg px-2 py-1.5 bg-white text-gray-700">
              <option value="">Todas as marcas</option>
              {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-surface-border rounded-lg px-2 py-1.5 bg-white text-gray-700">
              <option value="">Todos os status</option>
              {statusOptions.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
          ) : filteredTires.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem pneus cadastrados. Sincronize a base Finatto para popular esta tabela.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-4 py-2 font-medium">Nº fogo</th>
                  <th className="px-4 py-2 font-medium">Marca / Modelo</th>
                  <th className="px-4 py-2 font-medium">Medida</th>
                  <th className="px-4 py-2 font-medium">Placa</th>
                  <th className="px-4 py-2 font-medium">Vida</th>
                  <th className="px-4 py-2 font-medium text-right">KM</th>
                  <th className="px-4 py-2 font-medium text-right">mm</th>
                  <th className="px-4 py-2 font-medium text-right">Recap.</th>
                  <th className="px-4 py-2 font-medium text-right">Custo</th>
                  <th className="px-4 py-2 font-medium text-right">Custo/km</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {tireList.visible.map((t, i) => (
                  <tr key={i} className="text-gray-700">
                    <td className="px-4 py-2 font-medium text-gray-900">{t.fireNumber}</td>
                    <td className="px-4 py-2">{t.brand ?? "—"}{t.model ? ` · ${t.model}` : ""}</td>
                    <td className="px-4 py-2">{t.size ?? "—"}</td>
                    <td className="px-4 py-2">{t.plate ?? "—"}</td>
                    <td className="px-4 py-2">{t.lifeStage}</td>
                    <td className="px-4 py-2 text-right">{fmtNumber(t.kmRun)}</td>
                    <td className="px-4 py-2 text-right">{t.currentMm != null ? t.currentMm : "—"}</td>
                    <td className="px-4 py-2 text-right">{t.recapCount}</td>
                    <td className="px-4 py-2 text-right">{fmtBRL(t.totalCost)}</td>
                    <td className="px-4 py-2 text-right">{t.costPerKm != null ? fmtBRL(t.costPerKm) : "—"}</td>
                    <td className="px-4 py-2">{t.status ?? "—"}</td>
                  </tr>
                ))}
                <LoadMoreRow list={tireList} colSpan={11} label="pneus" capped={false} />
              </tbody>
            </table>
          )}
        </div>
      </details>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Gestão de Pneus — camada operacional (/fleet/...) + o painel do acervo acima.
// ════════════════════════════════════════════════════════════════════════════

type Aba = "visao" | "pneus" | "movimentacoes" | "inspecoes" | "recapagens" | "configuracao";

const STATUS_COLORS: Record<string, string> = {
  installed: "var(--brand)", stock: "#3B82F6", retread: "#F59E0B", written_off: "#9CA3AF", unknown: "#CBD5E1",
};
const LIFE_COLORS: Record<string, string> = { Novo: "#22C55E", R1: "#14B8A6", R2: "#3B82F6", "R3+": "#A855F7" };
/** Fundo/borda das linhas de "Principais alertas" por severidade. */
const SEV_BOX: Record<string, string> = {
  critical: "bg-danger-50 border-danger-100 text-danger-700",
  warning: "bg-warning-50 border-warning-100 text-warning-700",
  info: "bg-info-50 border-info-100 text-info-700",
};

export function TiresPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useTireCapabilities();
  const [mover, setMover] = useState<MoveTirePreset | null>(null);
  const [menu, setMenu] = useState(false);
  const aba = ((params.get("aba") as Aba) || "visao");
  const setAba = (a: Aba) => {
    const p = new URLSearchParams();
    if (a !== "visao") p.set("aba", a);
    setParams(p, { replace: true });
  };

  const ov = useQuery({
    queryKey: ["tires", "overview"],
    queryFn: () => api.get<TireOverviewDto>("/fleet/tires/overview"),
    staleTime: 60_000,
  });
  const k = ov.data?.kpis;
  const pct = (n: number) => (k && k.total > 0 ? `${((n / k.total) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}% do total` : undefined);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestão de Pneus"
        subtitle="Controle do ciclo de vida, localização, inspeções e desempenho dos pneus da frota."
        actions={
          <div className="flex gap-2 relative">
            {can("tires.move") && <button className="btn-secondary" onClick={() => setMover({})}>Movimentar pneu</button>}
            {(can("tires.inspect") || can("tires.retread") || can("tires.manage")) && (
              <div className="relative">
                <button className="btn-primary" onClick={() => setMenu((v) => !v)} aria-haspopup="menu" aria-expanded={menu}>
                  <IconPlus className="w-4 h-4" /> Novo registro
                </button>
                {menu && (
                  <div className="absolute right-0 mt-1 w-64 z-20 card py-1 text-sm" role="menu" onMouseLeave={() => setMenu(false)}>
                    {can("tires.inspect") && (
                      <button role="menuitem" className="w-full text-left px-4 py-2 hover:bg-surface-muted" onClick={() => navigate("/app/frota/pneus/inspecao")}>
                        Inspeção de pneus (ronda por veículo)
                      </button>
                    )}
                    {can("tires.move") && (
                      <button role="menuitem" className="w-full text-left px-4 py-2 hover:bg-surface-muted" onClick={() => { setMenu(false); setMover({ action: "to_retread" }); }}>
                        Envio para recapagem
                      </button>
                    )}
                    {can("tires.manage") && (
                      <button role="menuitem" className="w-full text-left px-4 py-2 hover:bg-surface-muted" onClick={() => { setMenu(false); setAba("configuracao"); }}>
                        Configuração de eixos / importação Finatto
                      </button>
                    )}
                    <p className="px-4 pt-2 pb-1 text-[11px] text-gray-400 border-t border-surface-border mt-1">
                      O cadastro do pneu continua na Finatto, e o SIAG registra a operação.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiTile label="Total de pneus" value={k ? fmtNumber(k.total) : "—"} footnote="Ativos (sem descartados)" loading={ov.isLoading}
          visual={k ? <ShareBar pct={100} color="#D1D5DB" /> : undefined} />
        <KpiTile label="Instalados" value={k ? fmtNumber(k.installed) : "—"} footnote={k ? pct(k.installed) : undefined} loading={ov.isLoading}
          visual={k && k.total > 0 ? <ShareBar pct={(k.installed / k.total) * 100} color="var(--brand)" /> : undefined} />
        <KpiTile label="Em estoque" value={k ? fmtNumber(k.stock) : "—"} footnote={k ? pct(k.stock) : undefined} loading={ov.isLoading}
          visual={k && k.total > 0 ? <ShareBar pct={(k.stock / k.total) * 100} color="#3B82F6" /> : undefined} />
        <KpiTile label="Em recapagem" value={k ? fmtNumber(k.retread) : "—"} footnote={k ? pct(k.retread) : undefined} loading={ov.isLoading}
          visual={k && k.total > 0 ? <ShareBar pct={(k.retread / k.total) * 100} color="#F59E0B" /> : undefined} />
        <KpiTile label="Em atenção" value={k ? fmtNumber(k.attention) : "—"} status={k && k.attention > 0 ? "warning" : null}
          tone={k && k.attention > 0 ? "warning" : null}
          footnote="Sulco perto do limite ou problema" loading={ov.isLoading}
          visual={k && k.total > 0 ? <ShareBar pct={(k.attention / k.total) * 100} color="#F59E0B" /> : undefined} />
        <KpiTile label="Críticos" value={k ? fmtNumber(k.critical) : "—"} status={k && k.critical > 0 ? "critical" : null}
          tone={k && k.critical > 0 ? "critical" : null}
          footnote="Sulco no limite ou dano grave" loading={ov.isLoading}
          visual={k && k.total > 0 ? <ShareBar pct={(k.critical / k.total) * 100} color="#EF4444" /> : undefined} />
      </div>
      {ov.isError && (
        <p className="text-sm text-warning-700 bg-warning-50 rounded-lg px-4 py-2">
          Os indicadores operacionais não carregaram agora, mas o painel do acervo abaixo continua disponível.
        </p>
      )}

      <TabBar<Aba>
        active={aba}
        onChange={setAba}
        tabs={[
          { key: "visao", label: "Visão Geral" },
          { key: "pneus", label: "Pneus", count: k ? k.total + k.writtenOff : null },
          { key: "movimentacoes", label: "Movimentações" },
          { key: "inspecoes", label: "Inspeções", count: ov.data?.coverage.inspectionsLast30d ?? null },
          { key: "recapagens", label: "Recapagens" },
          { key: "configuracao", label: "Configuração", hidden: !can("tires.manage") },
        ]}
      />

      {aba === "visao" && (
        <>
          <TiresOverviewTab data={ov.data} loading={ov.isLoading} onOpen={(id) => navigate(`/frota/pneus/${id}`)} />
          <div className="pt-4">
            <TireBiDashboard />
          </div>
        </>
      )}
      {aba === "pneus" && <TireListTab onMove={setMover} />}
      {aba === "movimentacoes" && <TireMovementsTab />}
      {aba === "inspecoes" && <TireInspectionsTab />}
      {aba === "recapagens" && <TireRetreadsTab onMove={setMover} />}
      {aba === "configuracao" && can("tires.manage") && <TireSetupTab />}

      {mover && <MoveTireModal preset={mover} onClose={() => setMover(null)} />}
    </div>
  );
}

function TiresOverviewTab({ data, loading, onOpen }: { data: TireOverviewDto | undefined; loading: boolean; onOpen: (id: string) => void }) {
  const statusDonut = (data?.byStatus ?? []).map((s) => ({ name: s.label, value: s.count, color: STATUS_COLORS[s.status] ?? NEUTRAL_SLICE }));
  const statusTotal = statusDonut.reduce((s, d) => s + d.value, 0);
  const lifeDonut = (data?.byLife ?? []).map((l) => ({ name: l.life, value: l.count, color: LIFE_COLORS[l.life] ?? NEUTRAL_SLICE }));
  const lifeTotal = lifeDonut.reduce((s, d) => s + d.value, 0);
  const sev: Record<string, string> = { critical: "badge-red", warning: "badge-amber", info: "badge-blue" };
  const sevLabel: Record<string, string> = { critical: "Crítico", warning: "Atenção", info: "Informativo" };

  return (
    <div className="space-y-4">
      <SectionHeader icon={<IconTire className="w-4 h-4" />} title="Operação no SIAG"
        subtitle="Situação, vida e alertas dos pneus da frota, com os dados mais recentes (não depende do período)">
        {data && <MetaChip>Pneus fora do SIAG seguem a situação da integração</MetaChip>}
      </SectionHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Situação dos pneus" subtitle="Todos os pneus, inclusive descartados" height={200} loading={loading} isEmpty={statusTotal === 0}>
          <DonutWithLegend data={statusDonut} total={statusTotal} />
        </ChartCard>
        <ChartCard title="Distribuição por vida" subtitle="Pneus ativos: nova, R1, R2, R3 ou mais" height={200} loading={loading} isEmpty={lifeTotal === 0}>
          <DonutWithLegend data={lifeDonut} total={lifeTotal} />
        </ChartCard>
        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Principais alertas</p>
            <p className="text-xs text-gray-400 mt-0.5">Sulco, inspeções dos últimos 30 dias e recapagens</p>
          </div>
          <div className="p-4 space-y-2 flex-1">
            {loading ? <div className="h-32 bg-gray-50 rounded-lg animate-pulse" />
              : (data?.alerts ?? []).length === 0 ? <p className="text-sm text-gray-500">Nenhum alerta agora.</p>
                : data!.alerts.map((a) => (
                  <div key={a.key} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${SEV_BOX[a.severity] ?? SEV_BOX.info}`}>
                    <span className="text-xl font-bold leading-none w-12 text-right tabular-nums">{fmtNumber(a.count)}</span>
                    <span className="flex-1 text-xs text-gray-700 leading-snug">{a.label}</span>
                    <span className={sev[a.severity]}>{sevLabel[a.severity]}</span>
                  </div>
                ))}
          </div>
          {/* Cobertura do controle no SIAG: antes era uma frase solta no rodapé
              da seção; aqui ocupa o espaço que sobrava no card e dá contexto
              para o tamanho dos alertas (poucas inspeções = pouco alerta). */}
          {data && (
            <div className="px-4 pb-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-2">Cobertura do controle</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Inspeções em 30 dias", value: data.coverage.inspectionsLast30d },
                  { label: "Pneus geridos no SIAG", value: data.kpis.managedBySiag },
                  { label: "Veículos com mapa de eixos", value: data.coverage.vehiclesWithConfiguration },
                  { label: "Montagens ativas", value: data.coverage.activeInstallations },
                ].map((c) => (
                  <div key={c.label} className="rounded-lg bg-surface-muted px-3 py-2">
                    <p className={`text-sm font-bold tabular-nums ${c.value === 0 ? "text-warning-600" : "text-gray-900"}`}>{fmtNumber(c.value)}</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{c.label}</p>
                  </div>
                ))}
              </div>
              <Link className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-tm-700 hover:underline" to="/frota/pneus?aba=configuracao">
                Configurar eixos e importação <IconChevronRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Pneus mais perto do limite</p>
            <p className="text-xs text-gray-400 mt-0.5">Menor folga entre o sulco atual e o limite de segurança</p>
          </div>
          {loading ? <div className="m-5 h-40 bg-gray-50 rounded-lg animate-pulse" />
            : (data?.nearLimit ?? []).length === 0 ? <p className="p-5 text-sm text-gray-500">Sem medição de sulco disponível.</p>
              : (
                <ul className="divide-y divide-gray-100">
                  {data!.nearLimit.map((t) => (
                    <li key={t.id}>
                      <button
                        className={`w-full text-left pl-4 pr-5 py-2.5 hover:bg-surface-muted flex items-center gap-3 border-l-[3px] ${
                          t.condition === "critical" ? "border-l-danger-500" : t.condition === "attention" ? "border-l-warning-500" : "border-l-transparent"
                        }`}
                        onClick={() => onOpen(t.id)}>
                        <span className="font-mono text-sm text-gray-900 w-24 truncate">{t.code}</span>
                        <span className="flex-1 min-w-0 text-xs text-gray-500 truncate">{t.plate ? `${t.plate}${t.positionLabel ? ` · ${t.positionLabel}` : ""}` : t.status === "installed" ? "Montado · placa não informada" : "Fora de veículo"}</span>
                        <span className="text-sm font-medium text-gray-800">{fmtMm(t.treadMm)}</span>
                        <ConditionBadge condition={t.condition} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
        </div>
        <ChartCard title="Valor de compra por marca" subtitle="Acervo ativo (fonte: cadastro da integração)" height={220} loading={loading} isEmpty={(data?.costByBrand ?? []).length === 0}
          emptyMessage="A integração não informa valor de compra.">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.costByBrand ?? []} layout="vertical" margin={{ left: 8, right: 16 }}>
              <defs>
                <linearGradient id="grad-tire-cost-brand" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.95} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f4" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`} />
              <YAxis type="category" dataKey="brand" width={90} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Valor"]} contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="url(#grad-tire-cost-brand)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="KM médio por marca" subtitle="Km acumulado informado pela integração (mín. 5 pneus por marca)" height={220} loading={loading}
          isEmpty={(data?.kmByBrand ?? []).length === 0} emptyMessage="Sem km acumulado confiável na integração.">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.kmByBrand ?? []} layout="vertical" margin={{ left: 8, right: 16 }}>
              <defs>
                <linearGradient id="grad-tire-km-brand" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor="#3B82F6" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.95} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f4" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`} />
              <YAxis type="category" dataKey="brand" width={90} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [`${fmtNumber(v)} km`, "KM médio"]} contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="avgKm" fill="url(#grad-tire-km-brand)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

    </div>
  );
}
